import {apiClient} from '@common/http/query-client';
import {getGuestSendDraftToken} from './guest-send-storage';

const GUEST_UPLOAD_URL_PARTS = [
  'file-entries',
  's3/',
  'tus/',
];

let interceptorInstalled = false;

export function setupGuestSendUploadAuth() {
  if (interceptorInstalled || typeof window === 'undefined') {
    return;
  }
  interceptorInstalled = true;

  apiClient.interceptors.request.use(config => {
    const token = getGuestSendDraftToken();
    const url = config.url || '';
    const needsGuestAuth = GUEST_UPLOAD_URL_PARTS.some(part =>
      url.includes(part),
    );

    if (token && needsGuestAuth) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });
}

export interface GuestInitiateResponse {
  otpRequired: boolean;
  pendingSendId?: number;
  otpExpiresAt?: string;
  message?: string;
  receiveUrl?: string;
  receivePath?: string;
  emailVerified?: boolean;
}

export async function createGuestSendDraft(email: string) {
  const response = await apiClient.post<{
    status?: string;
    draftToken: string;
    expiresAt: string;
    senderEmail: string;
    emailVerified: boolean;
  }>('guest-send/drafts', {email}, {timeout: 15000});

  const body = response.data;
  if (!body?.draftToken) {
    throw new Error('Could not start guest upload session.');
  }
  return body;
}

export async function initiateGuestSend(payload: {
  entryIds: number[];
  recipients: string[];
  subject: string | null;
  message: string | null;
}): Promise<GuestInitiateResponse> {
  const response = await apiClient.post<GuestInitiateResponse>(
    'guest-send/send/initiate',
    payload,
    {headers: guestSendAuthHeader()},
  );
  return response.data;
}

export async function confirmGuestSend(payload: {
  pendingSendId: number;
  code: string;
}): Promise<GuestInitiateResponse> {
  const response = await apiClient.post<GuestInitiateResponse>(
    'guest-send/send/confirm',
    payload,
    {headers: guestSendAuthHeader()},
  );
  return response.data;
}

export async function resendGuestSendOtp(pendingSendId: number) {
  const response = await apiClient.post<{message: string; otpExpiresAt: string}>(
    'guest-send/send/resend-otp',
    {pendingSendId},
    {headers: guestSendAuthHeader()},
  );
  return response.data;
}

export interface GuestSendQuotaResponse {
  maxBytes: number;
  usedBytes: number;
  remainingBytes: number;
  windowDays: number;
}

export async function fetchGuestSendQuota(
  email: string,
): Promise<GuestSendQuotaResponse> {
  const response = await apiClient.get<GuestSendQuotaResponse>(
    'guest-send/quota',
    {params: {email}},
  );
  return response.data;
}

function guestSendAuthHeader(): Record<string, string> {
  const token = getGuestSendDraftToken();
  if (!token) return {};
  return {Authorization: `Bearer ${token}`};
}
